import * as Tabs from '@radix-ui/react-tabs';
import { motion, AnimatePresence } from 'framer-motion';
import RolesTable from './RolesTable';
import { useState, useMemo } from 'react';
import { roles } from '@/lib/constants';
import RoleModal from './RoleModal';
import PermissionsContent from './PermissionsContent';
import SearchBar from 'components/shared/SearchBar';
import { FiPlus } from 'react-icons/fi';
import PermissionsContent2 from './PermissionsContent2';

interface Role {
    id: string;
    name: string;
    description: string;
    isActive: boolean;
    permissions: string[];
    createdAt: string;
}

interface RolesTabsProps {
    activeTab: string;
    onTabChange: (value: string) => void;
}

export default function RolesTabs({ activeTab, onTabChange }: RolesTabsProps) {
    const [rolesData, setRolesData] = useState<Role[]>(roles);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [mode, setMode] = useState<'create' | 'edit'>('edit');
    // Add filtered roles using useMemo
    const filteredRoles = useMemo(() => {
        if (!searchTerm) return rolesData;

        const searchLower = searchTerm.toLowerCase();
        return rolesData.filter(role =>
            role.name.toLowerCase().includes(searchLower) ||
            role.description.toLowerCase().includes(searchLower)
        );
    }, [rolesData, searchTerm]);

    const handleEdit = (role: Role) => {
        setMode('edit');
        setEditingRole({
            id: role.id,
            name: role.name,
            description: role.description,
            isActive: role.isActive,
            permissions: role.permissions,
            createdAt: role.createdAt
        });
        setIsEditModalOpen(true);
    };

    const handleUpdateRole = (updatedRole: Role) => {
        setRolesData(prevRoles =>
            prevRoles.map(role =>
                role.id === updatedRole.id ? updatedRole : role
            )
        );
    };

    return (
        <div>
            <Tabs.Root value={activeTab} onValueChange={onTabChange}>
            <div className="sticky top-0 pt-4 bg-black z-20">
            <div className="flex justify-between items-center mb-4">
                    <h1 className="text-xl font-semibold text-white">Roles and Permissions</h1>
                </div>
                    <Tabs.List className="flex w-full">
                        <Tabs.Trigger
                            value="roles"
                            className={`px-6 py-2.5 text-sm transition-colors relative flex-1 max-w-[200px] ${
                                activeTab === 'roles'
                                    ? 'bg-gradient-to-r from-[#65f4cdcc] to-[#4799e9b3] text-white rounded-lg'
                                    : 'bg-[#2A2A2A] text-gray-400 rounded-l-lg'
                            }`}
                        >
                            Roles
                        </Tabs.Trigger>
                        <Tabs.Trigger
                            value="permissions"
                            className={`px-6 py-2.5 text-sm transition-colors relative flex-1 max-w-[200px] ${
                                activeTab === 'permissions'
                                    ? 'bg-gradient-to-r from-[#65f4cdcc] to-[#4799e9b3] text-white rounded-lg'
                                    : 'bg-[#2A2A2A] text-gray-400 rounded-r-lg'
                            }`}
                        >
                            Permissions
                        </Tabs.Trigger>
                        <div className="flex-1 bg-[#2A2A2A] h-full" />
                    </Tabs.List>
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                    >
                        <Tabs.Content value="roles">
                            {activeTab === 'roles' && (
                                <>
                                    <div className="sticky top-[100px] pt-4 bg-black z-10 ">
                                        <div className='flex justify-between items-center bg-black pb-4'>
                                            <SearchBar
                                                value={searchTerm}
                                                onChange={setSearchTerm}
                                                placeholder="Search roles..."
                                            />
                                            <button
                                                onClick={() => {
                                                    setMode('create');
                                                    setIsEditModalOpen(true);
                                                }}
                                                className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white"
                                            >
                                                <FiPlus /> Add Role
                                            </button>
                                        </div>

                                        <div className="bg-[#2A2A2A] rounded-t-lg">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                                                        {/* Your table headers */}
                                                    </tr>
                                                </thead>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'roles' && (
                                <RolesTable
                                    roles={filteredRoles}
                                    onUpdateRole={handleUpdateRole}
                                    onEdit={handleEdit}
                                />
                            )}
                        </Tabs.Content>

                        <Tabs.Content value="permissions">
                            {activeTab === 'permissions' && <PermissionsContent2 />}
                        </Tabs.Content>
                    </motion.div>
                </AnimatePresence>
            </Tabs.Root>

            <RoleModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditingRole(null);
                    setMode('create');
                }}
                mode={mode}
                initialData={editingRole}
            />
        </div>
    );
} 